import { CSVLink } from 'react-csv';
import { Breadcrumb, Button, Input, Layout, Modal, Table, Tag } from 'antd';

import { ApprovalStepWithDetails, NodeWithDetails } from '@app/domain-t3-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';

import { useFindNodes } from '../data';
import { useNodeActions } from '../libs';

export type LandingPageItem = NodeWithDetails;

export const LandingPage = () => {
  const {
    getActionButton,
    getDeleteModalProps,
    getDetailsModalProps,
    toggleCreateModal,
    getCreateModalProps,
    getEditModalProps,
  } = useNodeActions();

  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<LandingPageItem>(
    'Nodes',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => getActionButton(record),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        width: 120,
        sorter: true,
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search nodes'),
      },
      {
        title: 'Approval Flow',
        dataIndex: 'approvalSteps',
        width: 300,
        render: (approvalSteps: ApprovalStepWithDetails[]) =>
          approvalSteps?.map((step) => <Tag key={step.id}>{step.stage.name}</Tag>),
      },
      {
        title: 'Updated By',
        dataIndex: ['updatedByUser', 'name'],
        width: 150,
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        width: 150,
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
      {
        title: 'Created By',
        dataIndex: ['createdByUser', 'name'],
        width: 150,
        ellipsis: true,
        sorter: true,
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search users'),
      },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 150,
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
        defaultSortOrder: 'descend',
      },
    ],
    {
      globalSearchFields: ['name'],
    },
  );

  const queryResult = useFindNodes<LandingPageItem>(
    getQuery({
      include: {
        createdByUser: true,
        updatedByUser: true,
        approvalSteps: {
          include: {
            stage: true,
          },
          orderBy: {
            id: 'asc',
          },
        },
      },
    }),
  );

  return (
    <>
      <Modal {...getCreateModalProps()} />
      <Modal {...getEditModalProps()} />
      <Modal {...getDeleteModalProps()} />
      <Modal {...getDetailsModalProps()} />
      <Breadcrumb style={{ margin: '16px 0' }} items={[{ title: 'Home' }, { title: 'Admin' }, { title: 'Nodes' }]} />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Nodes" />
        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} />,
              <Button {...getCreateButtonProps()} onClick={() => toggleCreateModal()} />,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink {...getCSVLinkProps(queryResult.data?.data)} />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult, { scroll: { y: 0 } })} />
      </Layout.Content>
    </>
  );
};
